import { Badge, fmt, RelativeTime, type Column, type RowAction } from "@nabble/ui";
import type { Organization } from "../../../api/adminOrganizations";
import type { AdminUser } from "../../../api/adminUsers";
import { highlightText } from "../../../components/highlightText";
import { STATUS_LABEL, STATUS_TONE, type UserStatus } from "../../../model/userStatus";
import { membershipSummary } from "./userDescriptions";

export interface UserColumnsInput {
  organizations: Organization[];
  actions: (user: AdminUser) => RowAction[];
}

function UserCell({ user: u, query }: { user: AdminUser; query: string }): React.JSX.Element {
  return (
    <div className="nb-cell-main">
      <span className="nb-avatar">{fmt.initials(u.displayName || u.email)}</span>
      <div className="nb-cell-stack">
        <strong title={u.displayName}>
          {highlightText(u.displayName, query)}
          {u.self ? (
            <>
              {" "}
              <Badge label="You" tone="info" dot={false} />
            </>
          ) : null}
        </strong>
        <span className="nb-cell-sub" title={u.email}>
          {highlightText(u.email, query)}
        </span>
      </div>
    </div>
  );
}

/** A comma-separated list, or a muted "None" when it is empty. */
function ListCell({ text }: { text: string }): React.JSX.Element {
  if (text === "") return <span className="nb-cell-muted">None</span>;
  return (
    <span className="nb-cell" title={text}>
      {text}
    </span>
  );
}

/** The columns of the Admin Center's user list, in display order. */
export function userColumns({ organizations, actions }: UserColumnsInput): Column<AdminUser>[] {
  const memberships = (u: AdminUser): string => membershipSummary(u, organizations);
  return [
    {
      key: "displayName",
      label: "User",
      minWidth: 260,
      text: (u) => `${u.displayName} ${u.email}`,
      render: (u, { query }) => <UserCell user={u} query={query} />,
    },
    {
      key: "status",
      label: "Status",
      type: "badge",
      width: 140,
      badge: (u) => ({ label: STATUS_LABEL[u.status], tone: STATUS_TONE[u.status] }),
      filter: { options: (Object.keys(STATUS_LABEL) as UserStatus[]).map((s) => ({ value: s, label: STATUS_LABEL[s] })) },
    },
    {
      key: "mfaEnrolled",
      label: "2FA",
      type: "badge",
      width: 136,
      badge: (u) => (u.mfaEnrolled ? { label: "Set up", tone: "success" } : { label: "Pending", tone: "warning" }),
      filter: {
        label: "2FA",
        options: [
          { value: "true", label: "Set up" },
          { value: "false", label: "Pending" },
        ],
      },
    },
    {
      key: "memberships",
      label: "Organizations and roles",
      minWidth: 260,
      sortable: false,
      text: memberships,
      render: (u) => <ListCell text={memberships(u)} />,
    },
    {
      key: "suiteAdmin",
      label: "Admin Center",
      type: "badge",
      width: 150,
      badge: (u) => (u.suiteAdmin ? { label: "Suite administrator", tone: "info" } : { label: "No", tone: "neutral" }),
      filter: {
        label: "Admin Center",
        options: [
          { value: "true", label: "Suite administrator" },
          { value: "false", label: "No" },
        ],
      },
    },
    {
      key: "lastLoginAt",
      label: "Last sign-in",
      type: "datetime",
      width: 150,
      text: (u) => (u.lastLoginAt ? fmt.dateTime(u.lastLoginAt) : "Never"),
      render: (u) => <RelativeTime value={u.lastLoginAt} fallback="Never" />,
    },
    { key: "actions", label: "Actions", type: "actions", width: 60, actions },
  ];
}
